import React, { useState, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import { useTranslate } from 'react-admin';
import { MaterialSymbol } from '../common/MaterialSymbol';
import { recommendationService, Recommendation } from '../../services/recommendationService';

interface SimilarCoursesProps {
    courseId: number;
    limit?: number;
    onCourseSelect?: (courseId: number) => void;
    onEnroll?: (courseId: number) => void;
}

/**
 * "Ähnliche Kurse"-Sektion im Design der Vorlage meine-kurse.html
 * (.similar/.simrow — Styles gescoped in Enrollments/my-courses-design.css,
 * derzeit einziger Verwender ist die Seite "Meine Kurse").
 */
const SimilarCourses: React.FC<SimilarCoursesProps> = ({
    courseId,
    limit = 3,
    onCourseSelect,
}) => {
    const translate = useTranslate();

    const [similar, setSimilar] = useState<Recommendation[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSimilarCourses();
    }, [courseId, limit]);

    const loadSimilarCourses = async () => {
        try {
            setLoading(true);
            const data = await recommendationService.getSimilarCourses(courseId, limit);
            setSimilar(data);
        } catch (err) {
            console.error('Failed to load similar courses:', err);
            setSimilar([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <section className="similar section-gap">
                <div className="sec-head">
                    <Skeleton variant="text" width={180} height={28} />
                </div>
                {[1, 2, 3].slice(0, limit).map((i) => (
                    <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 1, mb: 1 }} />
                ))}
            </section>
        );
    }

    if (similar.length === 0) {
        return null; // Don't show section if no similar courses
    }

    return (
        <section className="similar section-gap">
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className="ico"><MaterialSymbol name="schedule" /></span>
                    {translate('recommendations.similarCourses', { _: 'Ähnliche Kurse' })}
                </h2>
            </div>

            {similar.map((rec) => {
                const primaryReason = recommendationService.getPrimaryReason(rec);
                return (
                    <button
                        key={rec.course.id}
                        type="button"
                        className="simrow"
                        onClick={() => onCourseSelect?.(rec.course.id)}
                    >
                        <span className="tile"><MaterialSymbol name="school" size={26} /></span>
                        <div className="body">
                            <p className="ttl">{rec.course.title}</p>
                            <div className="row2">
                                <span className="lvl">
                                    {recommendationService.getDifficultyLabel(rec.course.difficulty)}
                                </span>
                                {rec.course.estimatedDuration ? (
                                    <span className="dur">
                                        <MaterialSymbol name="schedule" size={14} />
                                        {recommendationService.formatDuration(rec.course.estimatedDuration)}
                                    </span>
                                ) : null}
                            </div>
                            {primaryReason && (
                                <span className="katalog">
                                    <MaterialSymbol name="auto_awesome" size={14} />
                                    {primaryReason}
                                </span>
                            )}
                        </div>
                        <span className="go"><MaterialSymbol name="arrow_forward" size={20} /></span>
                    </button>
                );
            })}
        </section>
    );
};

export default SimilarCourses;
